import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
const useClickOutside = (refs, isOpen, onClose) => {
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (refs.some(ref => ref.current && ref.current.contains(e.target))) return;
            onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [refs, isOpen, onClose]);
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — Select
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const Select = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select an option...',
    disabled = false,
    className = '',
    style = {},
    icon,
    portal = true,
    variant = 'default', // 'default' | 'ghost'
    size = 'md' // 'md' | 'sm'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    
    useClickOutside([triggerRef, dropdownRef], isOpen, () => setIsOpen(false));

    const selectedOption = options.find(o => o.value === value);

    const getCoords = () => {
        if (!triggerRef.current) return { top: 0, left: 0, width: 200 };
        const rect = triggerRef.current.getBoundingClientRect();
        return { top: rect.bottom + 4, left: rect.left, width: rect.width };
    };

    const toggleOpen = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleSelect = (val) => {
        onChange(val);
        setIsOpen(false);
    };

    const DropdownContent = () => {
        const coords = getCoords();
        return (
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        ref={dropdownRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bg-[var(--bg-surface)]/85 backdrop-blur-xl border border-[var(--border)] rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex flex-col origin-top z-[var(--z-popover,5000)] max-h-[300px] overflow-hidden"
                        style={{ 
                            top: portal ? coords.top : 'calc(100% + 4px)', 
                            left: portal ? coords.left : 0, 
                            minWidth: portal ? Math.max(coords.width, size === 'sm' ? 60 : 120) : '100%',
                            position: portal ? 'fixed' : 'absolute'
                        }}
                    >
                        <div className="custom-scrollbar overflow-y-auto p-1 flex-1">
                            {options.map((opt) => (
                                <div
                                    key={opt.value}
                                    onClick={() => handleSelect(opt.value)}
                                    className={`px-2.5 py-1.5 text-[13px] rounded-lg cursor-pointer flex items-center gap-2 whitespace-nowrap transition-colors hover:bg-[var(--bg-muted)]/50 ${opt.value === value ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-medium' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                                >
                                    {opt.icon && <ObliqueIcon name={opt.icon} size={14} color="var(--text-tertiary)" />}
                                    <span className="flex-1">{opt.label}</span>
                                    {opt.value === value && <ObliqueIcon name="check" size={14} color="var(--accent)" />}
                                </div>
                            ))}
                            {options.length === 0 && (
                                <div className="py-2 px-2.5 text-xs text-[var(--text-tertiary)] text-center">No options available</div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    };

    return (
        <div className={`relative ${className}`} style={style}>
            <div
                ref={triggerRef}
                onClick={toggleOpen}
                className={`
                    flex items-center gap-1.5 transition-all select-none 
                    ${size === 'sm' ? 'h-7 px-2 rounded-md text-xs' : 'h-10 px-3 rounded-lg text-[13px]'}
                    ${variant === 'ghost' ? 'bg-transparent border border-transparent hover:bg-[var(--bg-subtle)]' : 'border bg-[var(--bg-surface)] border-[var(--border)] hover:border-[var(--border-strong)] shadow-sm'}
                    ${disabled ? 'opacity-50 cursor-not-allowed bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-tertiary)]' : 'cursor-pointer text-[var(--text-primary)] focus-within:ring-2 focus-within:ring-[var(--accent-glow)] focus-within:border-[var(--accent)]'} 
                    ${isOpen && variant !== 'ghost' ? 'border-[var(--accent)] ring-2 ring-[var(--accent-glow)]' : ''}
                `}
            >
                {icon && <ObliqueIcon name={icon} size={15} color="var(--text-tertiary)" />}
                <span className={`flex-1 overflow-hidden text-ellipsis whitespace-nowrap ${!selectedOption ? 'text-[var(--text-tertiary)]' : ''}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <div className={`flex items-center justify-center origin-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <ObliqueIcon name="keyboard_arrow_down" size={16} color="var(--text-tertiary)" />
                </div>
            </div>

            {portal && typeof document !== 'undefined' ? createPortal(<DropdownContent />, document.body) : <DropdownContent />}
        </div>
    );
};
